import type { CatalogueCategory, CatalogueProduct } from './product-port'

/** One catalogue row as Step 2 shows it. */
export interface CatalogueRow {
	productId: string
	code: string
	name: string
	/** The category's name, or `null` for an uncategorized product. */
	categoryName: string | null
}

const collator = new Intl.Collator('en', { sensitivity: 'base' })

/** Plain code-unit order (deterministic, locale-independent). */
const byCodeUnits = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0)

/**
 * The deterministic, unranked Step 2 order (TO-123 REQ-001): category name
 * (uncategorized last), then product name, then product code. Names compare
 * case- and accent-insensitively. The ranking follow-up (TO-157) replaces this.
 */
export function orderCatalogue(products: CatalogueProduct[], categories: CatalogueCategory[]): CatalogueRow[] {
	const categoryName = new Map(categories.map((c) => [c.categoryId, c.name]))
	const rows = products.map((p) => ({
		productId: p.productId,
		code: p.code,
		name: p.name,
		categoryName: p.categoryId === null ? null : (categoryName.get(p.categoryId) ?? null),
	}))
	return rows.sort((a, b) => {
		if (a.categoryName !== b.categoryName) {
			if (a.categoryName === null) return 1
			if (b.categoryName === null) return -1
			const byCategory = collator.compare(a.categoryName, b.categoryName)
			if (byCategory !== 0) return byCategory
		}
		return collator.compare(a.name, b.name) || byCodeUnits(a.code, b.code)
	})
}
